import cx from 'classnames';
import {
  ButtonHTMLAttributes,
  ElementType,
  forwardRef,
  MouseEvent,
} from 'react';
import Spinner from 'react-bootstrap/Spinner';
import css from './ButtonV2.module.scss';

export interface IButtonV2Props
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * For use when passing an icon. Defaults css rule is to justify center.
   */
  alignment?: 'left' | 'right';
  borderStyle?: 'dashed' | 'dotted' | 'solid';
  color?: 'black' | 'blue' | 'gray' | 'green' | 'orange' | 'red';
  icon?: ElementType;
  iconClassName?: string;
  iconColor?: 'black' | 'blue' | 'gray' | 'green' | 'orange' | 'red';
  iconPosition?: 'left' | 'right';
  iconSize?: number;
  isDisabled?: boolean;
  isLoading?: boolean;
  isPressed?: boolean;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'link' | 'outline' | 'outline-light' | 'solid' | 'transparent';
}

export const ButtonV2 = forwardRef<HTMLButtonElement, IButtonV2Props>(
  (
    {
      alignment,
      borderStyle,
      children,
      className,
      color = 'blue',
      disabled,
      icon: Icon,
      iconClassName,
      iconColor,
      iconPosition = 'left',
      iconSize = 14,
      isDisabled,
      isLoading,
      isPressed,
      onClick,
      size = 'md',
      variant = 'solid',
      ...rest
    },
    ref
  ) => {
    const isButtonDisabled = disabled || isDisabled || isLoading;

    const renderIcon = () => {
      return (
        <Icon
          aria-hidden="true"
          className={cx(
            css.icon,
            css[iconColor],
            css[iconPosition],
            iconClassName
          )}
          height={size === 'sm' ? 11 : iconSize}
          width={size === 'sm' ? 11 : iconSize}
        />
      );
    };

    return (
      <button
        {...rest}
        aria-disabled={isButtonDisabled}
        aria-pressed={isPressed}
        className={cx(
          css.button,
          css[color],
          css[size],
          css[variant],
          className,
          {
            'justify-content-start': Icon && alignment === 'left',
            'justify-content-end': Icon && alignment === 'right',
          }
        )}
        onClick={(ev: MouseEvent<HTMLButtonElement>) =>
          isButtonDisabled ? ev.preventDefault() : onClick?.(ev)
        }
        ref={ref}
        style={{ ...rest.style, borderStyle }}
      >
        <>
          <span className={cx(css.spinner, { [css.visible]: isLoading })}>
            <Spinner
              animation="border"
              aria-hidden="true"
              as="span"
              role="status"
              size="sm"
            />
          </span>
          {Icon && iconPosition === 'left' && renderIcon()}
          {children}
          {Icon && iconPosition === 'right' && renderIcon()}
        </>
      </button>
    );
  }
);

ButtonV2.displayName = 'ButtonV2';
